// src/hooks/useNumeroConfig.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import NumeroConfigModal from '../components/NumeroConfigModal';

/**
 * @typedef {Object} OnChangePayload
 * @property {'auto'|'manual'} modo
 * @property {string} prefijo
 * @property {number} siguiente
 */

/**
 * Hook para gestionar un número secuencial configurable en Firestore.
 *
 * @param {string} configKey  Clave de la colección/doc en Firestore.
 * @param {{ title?: string }} [options] Opciones (por ahora solo `title` para el modal).
 * @returns {{
 *   numero: string,
 *   modo: 'auto'|'manual',
 *   abrirConfiguracion: ()=>void,
 *   NumeroConfigModalUI: ()=>React.ReactElement|null
 * }}
 */
export function useNumeroConfig(configKey, options = {}) {
  const [modo, setModo] = useState('auto');
  const [prefijo, setPrefijo] = useState('');
  const [siguiente, setSiguiente] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Cargo la configuración inicial de Firestore
  useEffect(() => {
    async function fetchConfig() {
      const snap = await getDoc(doc(db, 'configuracion', configKey));
      if (snap.exists()) {
        const data = snap.data();
        setModo(data.modo || 'auto');
        setPrefijo(data.prefijo || '');
        setSiguiente(data.siguienteNumero || 0);
      }
    }
    fetchConfig();
  }, [configKey]);

  // Número formateado (solo en modo auto)
  const numero =
    modo === 'auto'
      ? `${prefijo}${String(siguiente).padStart(4, '0')}`
      : '';

  const abrirConfiguracion = useCallback(() => setIsOpen(true), []);
  const cerrarConfiguracion = useCallback(() => setIsOpen(false), []);

  /**
   * @param {OnChangePayload} payload
   */
  const handleOnChange = useCallback(
    async ({ modo: newModo, prefijo: newPrefijo, siguiente: newSig }) => {
      await setDoc(
        doc(db, 'configuracion', configKey),
        { modo: newModo, prefijo: newPrefijo, siguienteNumero: newSig },
        { merge: true }
      );
      setModo(newModo);
      setPrefijo(newPrefijo);
      setSiguiente(newSig);
      cerrarConfiguracion();
    },
    [configKey, cerrarConfiguracion]
  );

  // Componente modal ya "inyectado" con todas las props
  const NumeroConfigModalUI = useCallback(() => {
    if (!isOpen) return null;
    return (
      <NumeroConfigModal
        isOpen={isOpen}
        onClose={cerrarConfiguracion}
        configKey={configKey}
        title={options.title}
        modoInicial={modo}
        prefijoInicial={prefijo}
        siguienteInicial={siguiente}
        onChangeNumeroFormatted={handleOnChange}
      />
    );
  }, [
    isOpen,
    cerrarConfiguracion,
    configKey,
    options.title,
    modo,
    prefijo,
    siguiente,
    handleOnChange,
  ]);

  return {
    numero,
    modo,
    abrirConfiguracion,
    NumeroConfigModalUI,
  };
}
